import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Container from '../../components/layout/Container';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import ShareLinkButton from '../../components/product/ShareLinkButton';
import Skeleton from '../../components/common/Skeleton';
import Select from '../../components/common/Select';
import EmptyState from '../../components/common/EmptyState';
import { Package } from 'lucide-react';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { formatPrice } from '../../utils/priceCalculator';

// Catálogo do revendedor — mostra produtos das stores onde o user tem papel
// 'reseller' ativo, com preço cheio CONSUMER (price), preço com desconto que o
// revendedor paga (resellerPrice) e a comissão calculada (price - resellerPrice).
// O revendedor usa esta tela pra copiar link de produto e compartilhar.

const ResellerCatalog = () => {
  const { stores, isAuthenticated, loading: authLoading } = useAuth();
  const { error } = useToast();

  const resellerStores = useMemo(
    () => (stores || []).filter((s) => s.roleInStore === 'reseller' && s.membershipStatus === 'active'),
    [stores]
  );

  const [storeSlug, setStoreSlug] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (resellerStores.length && !storeSlug) setStoreSlug(resellerStores[0].slug);
  }, [resellerStores, storeSlug]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!storeSlug) return;
      setLoading(true);
      try {
        // Força o header X-Store-Slug via options, sem mexer no currentStore global.
        const data = await api.get('/api/products?limit=100', {
          headers: { 'X-Store-Slug': storeSlug },
          includeStore: false
        });
        if (!cancelled) setProducts(data?.rows || []);
      } catch (e) {
        if (!cancelled) error(e.message || 'Erro ao carregar produtos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [storeSlug, error]);

  if (authLoading) return <Container><Skeleton variant="block" height={120} /></Container>;
  if (!isAuthenticated) return <Navigate to="/entrar" replace />;

  if (resellerStores.length === 0) {
    return (
      <Container>
        <h1 className="text-3xl font-extrabold text-primary-dark mb-2">Catálogo do Revendedor</h1>
        <Card className="p-6 text-center text-app-text-secondary">
          Você ainda não é revendedor ativo em nenhuma loja. Candidate-se em uma loja reseller
          (<code>POST /api/stores/:id/resellers/apply</code>) e aguarde aprovação do dono.
        </Card>
      </Container>
    );
  }

  const currentStore = resellerStores.find((s) => s.slug === storeSlug);

  return (
    <Container>
      <div className="flex items-center justify-between mb-4">
        <div>
          <Link to="/revendedor/painel" className="text-sm text-primary hover:underline">← Painel</Link>
          <h1 className="text-3xl font-extrabold text-primary-dark">Catálogo do Revendedor</h1>
          <p className="text-sm text-app-text-secondary">
            Produtos que você pode revender com preço de compra e comissão.
          </p>
        </div>
        {resellerStores.length > 1 && (
          <div className="w-60">
            <Select
              value={storeSlug}
              onChange={(e) => setStoreSlug(e.target.value)}
              options={resellerStores.map((s) => ({ value: s.slug, label: s.name }))}
            />
          </div>
        )}
      </div>

      {currentStore && (
        <p className="text-xs text-app-text-secondary mb-4">
          Loja: <span className="font-semibold">{currentStore.name}</span> · {currentStore.businessType}
        </p>
      )}

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="block" height={360} />
          ))}
        </div>
      )}

      {!loading && products.length === 0 && (
        <Card>
          <EmptyState
            icon={Package}
            title="Sem produtos nesta loja"
            description="Volte em breve pra ver novidades."
          />
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => {
          // Escolhe a variação com maior desconto (price - resellerPrice).
          const bestVariation = (p.variations || []).reduce((best, v) => {
            const d = (parseFloat(v.price) || 0) - (parseFloat(v.resellerPrice) || 0);
            const bd = best ? (parseFloat(best.price) || 0) - (parseFloat(best.resellerPrice) || 0) : -1;
            return d > bd ? v : best;
          }, null);
          const price = parseFloat(bestVariation?.price || 0);
          const resellerPrice = parseFloat(bestVariation?.resellerPrice || 0);
          const discount = Math.max(0, Math.round((price - resellerPrice) * 100) / 100);
          const img = (p.images && p.images[0]) || null;

          return (
            <Card key={p.id} className="overflow-hidden">
              <div className="aspect-square bg-gray-100">
                <ImageWithFallback src={img} alt={p.name} fallbackText={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-app-text mb-1 line-clamp-2">{p.name}</h3>
                <p className="text-xs text-app-text-secondary mb-2 line-clamp-2">{p.description}</p>

                <div className="space-y-0.5 text-sm mb-3">
                  <div className="flex justify-between">
                    <span className="text-app-text-secondary">Preço cliente:</span>
                    <span className="font-medium">{formatPrice(price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-app-text-secondary">Você paga:</span>
                    <span className="font-semibold text-primary">{formatPrice(resellerPrice)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-success font-semibold">
                      <span>Comissão:</span>
                      <span>+{formatPrice(discount)}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Link to={`/fornecedor/${storeSlug}/produto/${p.id}`} className="flex-1">
                    <Button fullWidth size="sm" variant="outline">Ver produto</Button>
                  </Link>
                  <ShareLinkButton product={p} size="sm" variant="primary" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </Container>
  );
};

export default ResellerCatalog;
